import React, { useState } from 'react';
import { Dialog, Button, Notification, toast } from 'components/ui';
import InputModern from "components/ui/Input/InputModern";
import { 
  HiExclamationTriangle, 
  HiCheckCircle,
  HiTrash,
  HiXMark,
  HiArrowPath
} from 'react-icons/hi2';

const ConfirmActionDialog = ({
  isOpen,
  onClose,
  onConfirm,
  entity,
  name,
  action,
  isLoading = false,
  confirmationType = 'simple'
}) => {
  const [inputConfirmation, setInputConfirmation] = useState('');
  
  const textToConfirm = `${name}`;
  
  // Determine icon and color based on action type
  const getActionDetails = () => {
    switch(action.toLowerCase()) {
      case 'delete':
        return { 
          icon: <HiTrash className="h-12 w-12 text-red-500" />,
          color: 'red-600',
          bgColor: 'bg-red-50 dark:bg-red-900/20'
        };
      case 'activate':
        return { 
          icon: <HiCheckCircle className="h-12 w-12 text-emerald-500" />,
          color: 'emerald-600',
          bgColor: 'bg-emerald-50 dark:bg-emerald-900/20'
        };
      default:
        return { 
          icon: <HiExclamationTriangle className="h-12 w-12 text-amber-500" />,
          color: action === 'ACTIVATE' ? 'emerald-600' : 'red-600',
          bgColor: 'bg-amber-50 dark:bg-amber-900/20'
        };
    }
  };
  
  const { icon, color, bgColor } = getActionDetails();

  const handleConfirm = () => {
    if (confirmationType === 'double' && inputConfirmation !== textToConfirm) {
      toast.push(
        <Notification 
          title="You must write the exact phrase to confirm the action." 
          type="warning" 
        />, 
        { placement: "top-center" }
      );
      return;
    }

    onConfirm();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onRequestClose={onClose}
      overlayClassName="flex items-center justify-center w-full"
      closeButtonClassName="!hidden"
      contentClassName="max-w-md w-full mx-auto rounded-lg overflow-hidden shadow-xl transform transition-all !p-0"
    >
      <div className="relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
        >
          <HiXMark className="h-5 w-5" />
        </button>
        
        {/* Icon header */}
        <div className={`flex justify-center items-center py-6 ${bgColor}`}>
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full">
            {icon}
          </div>
        </div>
        
        <div className="p-6">
          <h3 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">
            {action} {entity}
          </h3>
          
          <div className="mt-3">
            <p className="text-base text-center text-gray-600 dark:text-gray-300">
              Are you sure you want to {action.toLowerCase()} <span className="font-medium">{name}</span>?
            </p>
            
            {confirmationType === 'double' && (
              <div className="mt-4">
                <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-2">
                  Type <span className="font-bold">{textToConfirm}</span> to confirm this action
                </p>
                <InputModern 
                  value={inputConfirmation} 
                  className="text-center"
                  placeholder={`${textToConfirm}`}
                  onChange={(e) => setInputConfirmation(e.target.value)} 
                />
              </div>
            )}
            
            <div className="mt-6 flex justify-center gap-3">
              <Button 
                variant="plain"
                size="md"
                icon={<HiXMark />}
                onClick={onClose}
                className="min-w-[100px]"
              >
                Cancel
              </Button>
              <Button 
                variant="solid"
                color={color}
                size="md"
                icon={isLoading ? <HiArrowPath className="animate-spin" /> : null}
                loading={isLoading} 
                onClick={handleConfirm}
                className="min-w-[100px]"
              >
                {action}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}; 

export default ConfirmActionDialog;